import type { LimbId } from "@rietz/shared";

/**
 * The maths behind hit reactions and the death fall — pure numbers, no three,
 * no allocation. A flinch is a damped spring; a fall is an inverted pendulum
 * about the feet. Kept here so both can be pinned by tests without a scene.
 */

/** One damped harmonic oscillator: deflection and its velocity. */
export interface Spring {
  x: number;
  v: number;
}

/** Gravity and the pendulum arm the body topples on, metres. */
export const G = 9.81;
export const TOPPLE_LEN = 1;
/** Flat on the ground. */
export const HALF_PI = Math.PI / 2;
/** Below this impact speed the body just stops; above it, one small bounce. */
export const BOUNCE_MIN_VEL = 1.5;
export const BOUNCE_RESTITUTION = 0.12;

/**
 * Semi-implicit Euler toward `target`, substepped so `omega * step <= 0.5` —
 * a stiff spring under a 100 ms frame must not explode.
 */
export function stepSpring(
  s: Spring,
  dt: number,
  omega: number,
  zeta: number,
  target = 0,
): void {
  if (dt <= 0 || omega <= 0) return;
  const n = Math.max(1, Math.ceil(dt / (0.5 / omega)));
  const h = dt / n;
  const k = omega * omega;
  const c = 2 * zeta * omega;
  for (let i = 0; i < n; i++) {
    s.v += (-k * (s.x - target) - c * s.v) * h;
    s.x += s.v * h;
  }
}

/** An impulse: velocity in, no displacement. */
export function kickSpring(s: Spring, dv: number): void {
  s.v += dv;
}

/**
 * The horizontal direction the body falls TOWARD, in the rig's own (yaw
 * rotated) frame: away from whoever did it. `from` is the shooter, `at` the
 * body. Degenerate input falls backward, local (0, +1) — yaw 0 faces −Z, so
 * +Z local is the back.
 */
export function fallDirLocal(
  fromX: number,
  fromZ: number,
  atX: number,
  atZ: number,
  yaw: number,
  out: { x: number; z: number },
): void {
  const dx = atX - fromX;
  const dz = atZ - fromZ;
  const len = Math.sqrt(dx * dx + dz * dz);
  if (len < 1e-3) {
    out.x = 0;
    out.z = 1;
    return;
  }
  const wx = dx / len;
  const wz = dz / len;
  // `rotation.y` takes local to world as (x·cos + z·sin, −x·sin + z·cos); this
  // is that transposed.
  const c = Math.cos(yaw);
  const s = Math.sin(yaw);
  out.x = c * wx - s * wz;
  out.z = s * wx + c * wz;
}

/** How a body goes down. */
export type FallStyle = "snap" | "topple" | "buckle" | "sag";

export interface DeathImpulse {
  style: FallStyle;
  /** Starting angular velocity about the feet, rad/s. */
  angVel: number;
}

/**
 * What the killing hit does to the body. A head shot snaps it over, a body
 * shot topples it, legs fold under it, and nobody's shot (bleed, starvation)
 * just sags.
 */
export function deathImpulseFor(limb: LimbId | null): DeathImpulse {
  switch (limb) {
    case "head":
      return { style: "snap", angVel: 3.6 };
    case "thorax":
    case "stomach":
      return { style: "topple", angVel: 2.3 };
    case "armL":
    case "armR":
      return { style: "topple", angVel: 1.7 };
    case "legL":
    case "legR":
      return { style: "buckle", angVel: 1.3 };
    default:
      return { style: "sag", angVel: 0.9 };
  }
}

export interface Topple {
  /** Radians off vertical; π/2 is flat. */
  angle: number;
  angVel: number;
  bounced: boolean;
  settled: boolean;
}

/**
 * One frame of the inverted pendulum. Substepped at 60 Hz so a long frame
 * cannot overshoot the ground by a metre. Never goes past flat or back past
 * upright; one bounce, then it lies still.
 */
export function stepTopple(t: Topple, dt: number): void {
  if (t.settled || dt <= 0) return;
  const n = Math.max(1, Math.ceil(dt * 60));
  const h = dt / n;
  const g = G / TOPPLE_LEN;
  for (let i = 0; i < n; i++) {
    t.angVel += g * Math.sin(t.angle) * h;
    t.angle += t.angVel * h;
    if (t.angle >= HALF_PI) {
      t.angle = HALF_PI;
      if (!t.bounced && t.angVel > BOUNCE_MIN_VEL) {
        t.angVel = -t.angVel * BOUNCE_RESTITUTION;
        t.bounced = true;
      } else {
        t.angVel = 0;
        t.settled = true;
        return;
      }
    } else if (t.angle < 0) {
      t.angle = 0;
      t.angVel = 0;
    }
  }
}
